import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  OverlayView,
  OverlayViewF,
  TrafficLayer,
} from "@react-google-maps/api";
import { Ambulance } from "../types/ambulance";
import hospitalIcon from "../Assets/emspin.png";
import { useTheme } from "../contexts/ThemeContext";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const HUNTINGTON_HOSPITAL = { lat: 40.8795, lng: -73.4163 };

// Define the libraries array according to the @react-google-maps/api type definitions
const libraries: any[] = ["places"];

type MapContainerProps = {
  ambulances: Ambulance[];
};

const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--background-secondary);
  box-shadow: var(--box-shadow);
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  color: var(--text-secondary);
  font-size: 18px;
`;

// Define the keyframe for pulsing animation
const pulseAnimation = `
  @keyframes vehiclePulse {
    0% {
      transform: translate(-50%, -50%) scale(0.5);
      opacity: 0.8;
    }
    70% {
      transform: translate(-50%, -50%) scale(2);
      opacity: 0;
    }
    100% {
      transform: translate(-50%, -50%) scale(0.5);
      opacity: 0;
    }
  }
`;

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const center = {
  lat: HUNTINGTON_HOSPITAL.lat,
  lng: HUNTINGTON_HOSPITAL.lng,
};

const options = {
  disableDefaultUI: true,
  zoomControl: true,
};

// Dark mode style for Google Maps
const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b9a76" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5b3" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#515c6d" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#17263c" }],
  },
];

// Helper function to get color based on ambulance state
const getAmbulanceColor = (ambulance: Ambulance): string => {
  if (ambulance.arrived) return "#4caf50"; // Green for arrived
  if (ambulance.possiblyArrived) return "#ff9800"; // Orange for possibly arrived
  return "#1976d2"; // Blue
};

// Helper function to get position for the ambulance marker
const getAmbulancePosition = (ambulance: Ambulance) => {
  // If the ambulance has arrived or possibly arrived, place it at the hospital
  if (ambulance.arrived || ambulance.possiblyArrived) {
    return HUNTINGTON_HOSPITAL;
  }
  // Otherwise use its actual location
  return { lat: ambulance.lat, lng: ambulance.lng };
};

const Map: React.FC<MapContainerProps> = ({ ambulances }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [selectedAmbulance, setSelectedAmbulance] = useState<Ambulance | null>(
    null
  );
  const { darkMode } = useTheme();

  const onMapLoad = React.useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMap(map);
  }, []);

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <LoadingContainer>Loading Google Maps...</LoadingContainer>;
  }

  return (
    <MapContainer>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={11.0}
        options={{
          ...options,
          styles: darkMode ? darkMapStyle : undefined,
        }}
        onLoad={onMapLoad}
      >
        {/* Hospital Marker */}
        <MarkerF
          position={HUNTINGTON_HOSPITAL}
          icon={{
            url: hospitalIcon,
            scaledSize: new window.google.maps.Size(40, 40),
          }}
        />

        {/* Ambulance Markers */}
        {ambulances
          .filter((ambulance) => ambulance.hasLocation)
          .map((ambulance) => {
            const position = getAmbulancePosition(ambulance);

            return (
              <OverlayViewF
                key={ambulance.unitId}
                position={position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{
                    position: "relative",
                    width: "50px",
                    height: "50px",
                  }}
                  onClick={() => setSelectedAmbulance(ambulance)}
                >
                  {/* Pulsing circle */}
                  <div
                    style={{
                      position: "absolute",
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      backgroundColor: `rgba(${
                        ambulance.arrived
                          ? "76, 175, 80"
                          : ambulance.possiblyArrived
                          ? "255, 152, 0"
                          : ambulance.isNew
                          ? "25, 118, 210"
                          : "25, 118, 210"
                      }, 0.4)`,
                      transform: "translate(-50%, -50%)",
                      animation: ambulance.isNew
                        ? "vehiclePulse 1.5s infinite"
                        : "vehiclePulse 2s infinite",
                    }}
                  />

                  {/* Center dot */}
                  <div
                    style={{
                      position: "absolute",
                      width: "12px",
                      height: "12px",
                      backgroundColor: getAmbulanceColor(ambulance),
                      border: "2px solid white",
                      borderRadius: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  />

                  {/* Label */}
                  <div
                    style={{
                      position: "absolute",
                      top: "20px",
                      left: "15%",
                      transform: "translateX(-50%)",
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                    }}
                  >
                    {ambulance.unitId}
                  </div>

                  <style>{pulseAnimation}</style>
                </div>
              </OverlayViewF>
            );
          })}
        <TrafficLayer />
      </GoogleMap>
    </MapContainer>
  );
};

export default Map;
